import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import ServiceModal from "@/components/ServiceModal";
import { useState } from "react";
import { Activity, Shield, Baby, Ship, Brain, Target, Building, Compass, Plane, Search, PiggyBank, Pill, Dog, BarChart3, Cpu, Users, Stethoscope, ChevronRight, X } from "lucide-react";
import telemedicineIcon from "/lovable-uploads/telemedicine-care-icon.png";
import wellnessIcon from "/lovable-uploads/wellness-preventive-health-icon.png";
import nextGenIcon from "/lovable-uploads/next-gen-healthtech-icon.png";
import advisoryIcon from "/lovable-uploads/strategy-advisory-icon.png";
import { useTranslation } from "@/hooks/useTranslation";
const Services = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [isOtherSectionHovered, setIsOtherSectionHovered] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const serviceDetails = {
    "Chronic Disease Telemedicine": {
      title: "Chronic Disease Telemedicine",
      description: "Digital tools for continuous management of chronic diseases, enabling patients to live healthier lives while healthcare professionals can monitor, intervene, and adapt care remotely.",
      videoUrl: "https://www.youtube.com/embed/wow5AGRB9Eo",
      imageUrl: "/lovable-uploads/b4afb37f-dd9e-499b-874e-91b7c1f0c1ad.png",
      features: [{
        title: "Health record",
        description: "Personalized medical file with profile, history, biological signal recordings, medication, and more."
      }, {
        title: "Telemetry",
        description: "Wireless monitoring of vital signs with real-time cloud transmission"
      }, {
        title: "Alerts & notifications",
        description: "Personalised alerts, reminders, and notifications for patients and clinicians"
      }, {
        title: "Tailored digital environments",
        description: "Dedicated mobile app, web platform, and dashboards for patients and healthcare professionals"
      }, {
        title: "Care plan management",
        description: "Structured care plans covering medication adherence, activity goals, and follow-up scheduling"
      }, {
        title: "Remote consultations",
        description: "Audio/video communication, chat, secure file sharing, and online consultation"
      }]
    },
    "Digital Outpatient Clinic": {
      title: "Digital Outpatient Clinic",
      description: "The Digital Outpatient Clinic transforms routine visits and follow-ups into secure, convenient online interactions. Patients connect with their doctors from anywhere, while clinicians manage appointments, records, and care plans on a single platform.",
      imageUrl: "/lovable-uploads/0ba642e9-4336-4908-8150-f49a661479cb.png",
      features: [{
        title: "Remote consultations",
        description: "Chat, video, and secure messaging for direct patient–doctor communication."
      }, {
        title: "Scheduling & management",
        description: "Integrated calendar for booking, reminders, and follow-up visits."
      }, {
        title: "Integrated health data",
        description: "Brings together health records, questionnaires, and medical files in one place."
      }, {
        title: "Customisable environments",
        description: "Flexible workflows tailored to different specialties (e.g. cardiology, endocrinology, dermatology)."
      }, {
        title: "e-Prescriptions & medication management",
        description: "Enables doctors to issue prescriptions digitally."
      }, {
        title: "Billing support",
        description: "Optional module for handling patient payments."
      }]
    },
    "Emergency & Panic Button": {
      title: "Emergency & Panic Button",
      description: "Immediate access to help in urgent situations. With a single press, patients can alert caregivers, clinicians, or emergency services, ensuring rapid response when it matters most.",
      imageUrl: "/lovable-uploads/2443acd8-5607-4550-b993-245cbefbf190.png",
      features: [{
        title: "One-touch alert",
        description: "Instant call for help through dedicated panic button."
      }, {
        title: "Real-time location",
        description: "GPS tracking to guide caregivers or emergency responders to the user's position."
      }, {
        title: "Geo-zones & notifications",
        description: "Configurable safety zones; alerts are sent if the wearer moves outside predefined limits."
      }, {
        title: "Fall detection",
        description: "Automatic detection of falls and alert transmission, even if the user cannot press the button."
      }, {
        title: "Integrated communication",
        description: "Direct voice or video connection with clinicians, call centers, or family members."
      }, {
        title: "Custom escalation paths",
        description: "Configurable workflows defining who is notified first (family, caregiver, emergency team)."
      }]
    },
    "Mental Health Support": {
      title: "Mental Health Support",
      description: "Accessible digital services that complement clinical, enabling continuous assessment, structured interventions, and improved patient engagement.",
      imageUrl: "/lovable-uploads/mental-health-support.png",
      features: [{
        title: "Clinical assessment tools",
        description: "Validated questionnaires and mood-tracking instruments for early detection and ongoing evaluation."
      }, {
        title: "Remote counselling",
        description: "Secure chat or video sessions with psychologists, psychiatrists, or certified coaches."
      }, {
        title: "Educational resources",
        description: "Scientifically validated resources and psychoeducational content to support adherence and self-management."
      }, {
        title: "Risk management",
        description: "Configurable alerts for crisis situations, ensuring timely escalation to clinicians or caregivers."
      }, {
        title: "Treatment planning",
        description: "Tools for clinicians to set therapy goals, assign digital exercises, and monitor adherence."
      }, {
        title: "Progress tracking",
        description: "Longitudinal monitoring of symptom progression and treatment response through structured dashboards."
      }]
    },
    "Pregnancy Monitoring & Support": {
      title: "Pregnancy Monitoring & Support",
      description: "Digital health solutions that accompany mothers through every stage of pregnancy, combining monitoring, education, and expert access for safer and more confident journeys.",
      imageUrl: "/lovable-uploads/cc9de6d6-1f80-403a-a659-4f71f6172738.png",
      features: [{
        title: "Connected devices & wearables",
        description: "Track vital signs (blood pressure, heart rate, glucose, weight) and fetal well-being with medical-grade devices."
      }, {
        title: "Educational resources",
        description: "Access to evidence-based content, tips, and guidelines tailored to each stage of pregnancy."
      }, {
        title: "Expert connection",
        description: "Direct communication with gynecologists, midwives, or other specialists for reassurance and timely guidance."
      }, {
        title: "Care plan & reminders",
        description: "Personalized schedules for check-ups, tests, nutrition, exercise, and medication adherence."
      }, {
        title: "Digital diary",
        description: "Mothers can log daily symptoms, moods, and lifestyle habits for more complete care insights."
      }]
    },
    "Marine Telemedicine": {
      title: "Marine Telemedicine",
      description: "Healthcare at sea is challenging due to distance, limited resources, and intermittent connectivity. Marine telemedicine bridges this gap by equipping ships with tools for first-line care",
      imageUrl: "/lovable-uploads/0b2a75f8-73ff-45bf-bf26-d69424b5d617.png",
      features: [{
        title: "Crew-initiated care",
        description: "Designated officers act as first responders, supported by remote specialists."
      }, {
        title: "Offline-first design",
        description: "Medical data are recorded during the incident and synchronized with experts once internet access is restored."
      }, {
        title: "Connected medical devices",
        description: "On-board diagnostic tools (ECG, oximeter, blood pressure monitors) capture vital signs for remote review."
      }, {
        title: "Image & file exchange",
        description: "Photos, scans, or other documents can be shared with specialists to support accurate evaluation."
      }, {
        title: "Remote consultations",
        description: "Video or messaging with on-shore doctors for triage, acute care, or chronic condition management."
      }, {
        title: "Logistics integration",
        description: "Coordination with ports, clinics, and evacuation services when on-site care is required."
      }]
    },
    "InsurTech": {
      title: "InsurTech",
      description: "",
      imageUrl: "/lovable-uploads/d6809d10-7f85-4170-9330-19ed0fd80db8.png",
      features: [{
        title: "Teleconsultations",
        description: "Online medical sessions included in insurance packages, improving access and lowering costs."
      }, {
        title: "Tele-underwriting",
        description: "Remote health assessment for faster, more accurate risk stratification and approval of insurance policies."
      }, {
        title: "Personalized care",
        description: "Focus on prevention, early diagnosis, and remote monitoring to reduce claims and improve outcomes."
      }, {
        title: "Customer experience",
        description: "Branded or white-label mobile apps that enhance accessibility, loyalty, and user satisfaction."
      }, {
        title: "Data-driven risk management",
        description: "Advanced analytics that improve underwriting accuracy, forecast costs, and support smarter product design."
      }, {
        title: "New business models",
        description: "Enable insurers to design preventive, value-based products that expand service offerings and competitiveness."
      }]
    },
    "Digital Animal Health": {
      title: "Digital Animal Health",
      description: "From pets at home to herds in the field, digital tools are transforming veterinary care. Our solutions bring monitoring, records, and expert guidance into one connected environment.",
      imageUrl: "/lovable-uploads/4b9f520c-83c0-4c4d-8922-9e137d791ad0.png",
      additionalImageUrl: "/lovable-uploads/ab52c4db-f5e6-422d-af2d-14bea0fdbc15.png",
      features: [{
        title: "Tele-veterinary consultations",
        description: "Book and manage veterinary appointments online, for check-ups, follow-up, and urgent situations."
      }, {
        title: "Digital health records",
        description: "Comprehensive animal profiles including vaccination schedules, treatments, nutrition, and diagnostics."
      }, {
        title: "Remote guidance for care",
        description: "Digital instructions and follow-up plans for nutrition, recovery, and preventive routines after veterinary visits."
      }, {
        title: "Population-level monitoring",
        description: "Analytics for livestock herds, shelters and breeding programs, supporting disease prevention and welfare management."
      }, {
        title: "Integration with veterinary pharma",
        description: "Companion apps linked with treatments to support adherence and track outcomes."
      }, {
        title: "Data analytics and reporting",
        description: "Early warning systems for disease outbreaks, performance tracking, and optimized care strategies."
      }]
    },
    "Digital Therapeutics & Pharma": {
      title: "Digital Therapeutics & Pharma",
      description: "When medicine leaves the box, it becomes digital. We provide software interventions and companion apps that extend treatment, improve adherence, and generate real-world evidence.",
      imageUrl: "/lovable-uploads/7a761384-25f9-43a3-8654-b21a8094757b.png",
      features: [{
        title: "Companion apps for drugs",
        description: "Branded or white-label applications that guide patients in medication use, provide reminders, and deliver tailored educational content."
      }, {
        title: "Evidence-based digital therapeutics",
        description: "Clinically validated apps and interventions addressing specific conditions such as diabetes, insomnia, or depression."
      }, {
        title: "Medication adherence support",
        description: "Pill reminders, digital diaries, and connected devices that reinforce treatment compliance."
      }, {
        title: "Clinical trial support (eCRF modules)",
        description: "Electronic case report forms with integrated questionnaires, symptom tracking, and digital monitoring, designed to simplify trial data collection."
      }, {
        title: "Analytics & reporting",
        description: "Automated analysis of patient-reported outcomes and clinical data, generating regulatory-ready reports."
      }, {
        title: "Patient engagement",
        description: "Interactive features that motivate patients through feedback, progress tracking, and communication with trial coordinators or care teams."
      }]
    },
    "Eco-Health Analytics": {
      title: "Eco-Health Analytics",
      description: "Environmental conditions shape human health. Eco-Health analytics uncovers these links by combining environmental and clinical information to reveal patterns, predict risks, and guide smarter decisions for cities and industry.",
      imageUrl: "/lovable-uploads/1a599d61-0148-4686-b50a-ec8eeddbcf65.png",
      features: [{
        title: "Public datasets",
        description: "Import and process datasets from established environmental indicator databases."
      }, {
        title: "Environmental data feeds",
        description: "Data streams import from environmental sensors measuring air quality, pollutants or climate indicators from sensor networks and open APIs to enrich health analytics."
      }, {
        title: "Health–environment correlation",
        description: "Analysis of combined datasets to identify patterns, trends, and risk factors that link environmental exposures with health outcomes"
      }, {
        title: "Trend analysis and alerts",
        description: "Monitoring of long-term exposure levels and automatic alerts for high-risk events such as heatwaves or pollution peaks."
      }, {
        title: "Decision support for cities and industry",
        description: "Provide evidence-based insights to guide urban planning, occupational health, and corporate sustainability strategies."
      }, {
        title: "Visualisation and dashboards",
        description: "Interactive maps and dashboards that display environmental indicators alongside health outcomes for easier interpretation."
      }]
    },
    "AI Decision Support": {
      title: "AI Decision Support",
      description: "Intelligent clinical assistance powered by advanced AI to enhance decision-making, streamline workflows, and improve patient outcomes.",
      additionalImageUrl: "/lovable-uploads/ea404312-774e-4545-9431-b60a559d95ee.png",
      features: [{
        title: "AI chart review",
        description: "Retrieves and summarizes relevant items from the patient profile so doctors can scan exams, history, medications, and notes quickly."
      }, {
        title: "Contactless vital signs",
        description: "Measure heart rate and wellness indicators through the phone camera using advanced rPPG technology. In just one short scan, patients get insights as easily as taking a selfie."
      }, {
        title: "Risk assessment and prediction",
        description: "Questionnaire based scoring and AI models, paired with tailored lifestyle advice."
      }, {
        title: "Symptom checker",
        description: "Patient entered symptoms evaluated by AI with suggested actions and guidance on when to seek care."
      }, {
        title: "Clinical decision support",
        description: "Models that flag deterioration risk, propose next steps, and support preventive and personalized interventions."
      }, {
        title: "Business intelligence",
        description: "Dashboards that analyze activity across facilities to optimize staffing, discharge planning, and follow up while tracking readmissions and costs."
      }]
    },
    "Digital Health Consulting": {
      title: "Digital Health Consulting",
      description: "Strategic consulting and expert guidance for healthcare organizations navigating digital transformation and innovation.",
      additionalImageUrl: "/lovable-uploads/0e9fd9f5-33a8-4929-af49-613c984aadb9.png",
      features: [{
        title: "Digital health strategy",
        description: "Development of roadmaps for hospitals, municipalities, and insurers to adopt and scale digital solutions."
      }, {
        title: "Interoperability and integration",
        description: "Guidance on standards such as HL7 FHIR, DICOM, and openEHR to ensure systems communicate securely and effectively."
      }, {
        title: "Process redesign",
        description: "Support in restructuring clinical and administrative workflows to align with digital transformation goals."
      }, {
        title: "Regulatory compliance",
        description: "Expertise on MDR, GDPR, and national frameworks to ensure projects meet legal and ethical requirements."
      }, {
        title: "Capacity building",
        description: "Training and support programs for clinicians, administrators, and technical teams."
      }, {
        title: "Evaluation and KPIs",
        description: "Design of monitoring frameworks with measurable indicators for performance, efficiency, and impact."
      }]
    },
    "DRG decision support": {
      title: "DRG decision support",
      description: "Diagnosis-Related Groups (DRGs) are more than a reimbursement tool. Used effectively, they become a decision support mechanism that improves cost control, transparency, and trust across insurers, providers, and patients, while also supporting hospitals in accurate coding and auditing.",
      imageUrl: "/lovable-uploads/b89a7215-9760-4b02-ba42-d1219d9353c6.png",
      features: [{
        title: "Cost optimization",
        description: "Standardized costing of medical cases by diagnosis and clinical severity reduces variability and makes healthcare expenses more predictable."
      }, {
        title: "Transparent pricing",
        description: "Supports insurers, providers, and patients with reliable, comparable costing models that strengthen trust and competitiveness."
      }, {
        title: "Risk assessment for insurance",
        description: "Improves risk estimation for both group and individual health plans, helping design sustainable insurance products."
      }, {
        title: "Hospital coding support",
        description: "Assists clinicians and coders with correct and consistent recording of cases based on ICD and GPC classifications."
      }, {
        title: "Audit and quality control",
        description: "Automated audit mechanisms identify weaknesses in medical record coding and support early corrective action."
      }, {
        title: "Targeted improvements",
        description: "Audit results guide interventions such as staff training, updates to operational guidelines, and coding refinements."
      }]
    },
    "Behavioural Change Solutions": {
      title: "Behavioural Change Solutions",
      description: "Changing habits is at the heart of prevention. Behavioural change solutions combine digital tools with evidence-based methods to motivate healthier lifestyles and support long-term adherence.",
      videoUrl: "https://www.youtube.com/embed/zR-W0YCqQxU",
      imageUrl: "/lovable-uploads/behavioral-change-app.png",
      features: [{
        title: "Personalized programs",
        description: "Tailored plans for nutrition, activity, and lifestyle change based on individual needs."
      }, {
        title: "Goal setting and reminders",
        description: "Structured objectives with smart notifications that keep users engaged and on track."
      }, {
        title: "Daily monitoring",
        description: "Daily tracking of steps, activity, weight, and other key wellness indicators."
      }, {
        title: "Motivational feedback",
        description: "Encouragement and real-time insights that sustain long-term progress."
      }, {
        title: "Educational content",
        description: "Evidence based resources that explain the science behind healthy choices."
      }, {
        title: "Progress visualization",
        description: "Clear charts and dashboards that highlight milestones and overall improvement."
      }]
    },
    "Corporate Wellbeing": {
      title: "Corporate Wellbeing",
      description: "Healthy workplaces create stronger businesses. Corporate wellbeing programs improve employee health, reduce absenteeism, and build a culture of engagement and resilience.",
      imageUrl: "/lovable-uploads/corporate-wellbeing-app.png",
      features: [{
        title: "Branded platforms",
        description: "Employer branded platforms that promote wellbeing across the workforce."
      }, {
        title: "Health programs",
        description: "Programs supporting stress management, physical activity, and mental health."
      }, {
        title: "Engagement tools",
        description: "Digital tools that encourage participation through reminders and gamification."
      }, {
        title: "Lifestyle resources",
        description: "Access to resources for healthier nutrition and lifestyle choices."
      }, {
        title: "Workforce analytics",
        description: "Analytics that help HR track participation, outcomes, and return on investment."
      }, {
        title: "Policy integration",
        description: "Alignment with occupational health and safety policies already in place."
      }]
    },
    "Scientific Wellness": {
      title: "Scientific Wellness",
      description: "Prevention enters the era of precision. Scientific wellness integrates genetic data, biomarkers, and digital health metrics to create individualized wellness strategies rooted in science.",
      features: []
    },
    "eHealth & Wellness Tourism": {
      title: "eHealth & Wellness Tourism",
      description: "Tourism and health intersect in new ways. eHealth and wellness tourism combines preventive check-ups and wellness programs with travel experiences, offering visitors long-term digital follow-up after their stay.",
      videoUrl: "https://www.youtube.com/embed/Anke5PpHTz4",
      features: []
    }
  };
  const handleLearnMore = (serviceTitle: string) => {
    setSelectedService(serviceTitle);
    setIsModalOpen(true);
  };
  const serviceCategories = [{
    id: "telemedicine",
    title: t.services.categories.telemedicine.title,
    description: t.services.categories.telemedicine.description,
    icon: () => <img src={telemedicineIcon} alt="Telemedicine" className="w-80 h-80 object-contain" />,
    gradient: "from-primary to-secondary",
    services: t.services.categories.telemedicine.services.map((service, index) => ({
      key: [
        "Chronic Disease Telemedicine",
        "Digital Outpatient Clinic",
        "Emergency & Panic Button",
        "Pregnancy Monitoring & Support",
        "Mental Health Support",
        "Marine Telemedicine"
      ][index],
      title: service.title,
      description: service.description,
      icon: [Activity, Shield, Shield, Baby, Brain, Ship][index],
      comingSoon: [false, true, true, true, false, false][index]
    }))
  }, {
    id: "wellness",
    title: t.services.categories.wellness.title,
    description: t.services.categories.wellness.description,
    icon: () => <img src={wellnessIcon} alt="Wellness & Preventive Health" className="w-80 h-80 object-contain" />,
    gradient: "from-accent-orange to-primary",
    services: t.services.categories.wellness.services.map((service, index) => ({
      key: [
        "Behavioural Change Solutions",
        "Corporate Wellbeing",
        "Scientific Wellness",
        "eHealth & Wellness Tourism"
      ][index],
      title: service.title,
      description: service.description,
      icon: [Target, Building, Compass, Plane][index],
      comingSoon: true
    }))
  }, {
    id: "smarthealth",
    title: t.services.categories.smartHealth.title,
    description: t.services.categories.smartHealth.description,
    icon: () => <img src={nextGenIcon} alt="Next-Gen HealthTech" className="w-80 h-80 object-contain" />,
    gradient: "from-primary to-accent-orange",
    services: t.services.categories.smartHealth.services.map((service, index) => ({
      key: [
        "InsurTech",
        "Digital Therapeutics & Pharma",
        "Digital Animal Health",
        "Eco-Health Analytics",
        "AI Decision Support",
        "DRG decision support"
      ][index],
      title: service.title,
      description: service.description,
      icon: [PiggyBank, Pill, Dog, BarChart3, Cpu, BarChart3][index],
      comingSoon: [true, false, true, true, true, true][index]
    }))
  }, {
    id: "advisory",
    title: t.services.categories.advisory.title,
    description: t.services.categories.advisory.description,
    icon: () => <img src={advisoryIcon} alt="Strategy & Advisory" className="w-80 h-80 object-contain" />,
    gradient: "from-accent-orange to-secondary",
    services: t.services.categories.advisory.services.map((service, index) => ({
      key: [
        "Digital Health Consulting"
      ][index],
      title: service.title,
      description: service.description,
      icon: Users,
      comingSoon: false
    }))
  }];
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-gradient-to-r from-accent-teal/5 to-transparent" />
        
        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="text-center mb-16">
              <h1 className="heading-xl mb-8 py-[10px] text-accent-teal">
                {t.services.hero.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed py-[2px]">
                {t.services.hero.subtitle}
              </p>
            </motion.div>

            {/* Services Categories - Horizontal Expandable Sections */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="space-y-8">
              

              {/* Desktop: Horizontal expandable layout */}
              <div className="hidden lg:flex gap-6 h-[540px] group/sections">
                {[serviceCategories.find(cat => cat.id === "telemedicine"), serviceCategories.find(cat => cat.id === "wellness"), serviceCategories.find(cat => cat.id === "smarthealth"), serviceCategories.find(cat => cat.id === "advisory")].filter(Boolean).map((category, index) => {
                const CategoryIcon = category!.icon;
                const solidColors = ['rgba(120, 198, 194, 0.35)', 'rgba(102, 179, 187, 0.35)', 'rgba(200, 221, 255, 0.35)', 'rgba(134, 142, 173, 0.35)'];
                const isExpanded = expandedSection === category!.id;
                return <motion.div key={category!.id} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 100
                }} className={`group relative rounded-[2rem] overflow-hidden transition-all duration-700 ease-in-out flex-1 shadow-lg cursor-pointer ${isExpanded ? 'flex-[7] shadow-2xl' : ''}`} onClick={() => {
                  if (isExpanded) {
                    setExpandedSection(null);
                    setIsOtherSectionHovered(false);
                  } else {
                    setExpandedSection(category!.id);
                    if (category!.id !== "telemedicine") setIsOtherSectionHovered(true);
                  }
                }}>
                      {/* Background solid color */}
                      <div className="absolute inset-0 opacity-95" style={{ backgroundColor: solidColors[index] }} />
                      
                        {/* Removed decorative animated shapes to eliminate dots */}


                      {/* Content container */}
                      <div className="relative h-full flex flex-col p-8 overflow-hidden">
                        {/* Collapsed state - vertical layout */}
                        <div className={`flex flex-col items-center justify-center h-full space-y-20 transition-all duration-500 relative ${isExpanded ? 'hidden' : ''}`}>
                          <ChevronRight 
                            className="w-8 h-8 absolute top-4 right-4 flex-shrink-0 hover:scale-110 transition-transform pointer-events-none" 
                            strokeWidth={1.5} 
                            style={{ 
                              color: category!.id === "telemedicine" ? '#78C6C2' : 
                                     category!.id === "wellness" ? '#66B3BB' :
                                     category!.id === "smarthealth" ? '#8BA4CD' :
                                     category!.id === "advisory" ? '#868EAD' : '#00858f'
                            }}
                          />
                          <div>
                            {category!.id === "telemedicine" && expandedSection && expandedSection !== "telemedicine" ? (
                              <Stethoscope className="w-20 h-20 drop-shadow-2xl" strokeWidth={1.5} style={{ color: '#78C6C2' }} />
                            ) : category!.id === "telemedicine" ? (
                              <motion.img 
                                src={telemedicineIcon} 
                                alt="Telemedicine" 
                                className="w-60 h-60 object-contain drop-shadow-2xl -mb-8"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                whileHover={{ scale: 1.05, rotate: 2 }}
                              />
                            ) : category!.id === "wellness" && expandedSection && expandedSection !== "wellness" ? (
                              <Activity className="w-20 h-20 drop-shadow-2xl" strokeWidth={1.5} style={{ color: '#66B3BB' }} />
                            ) : category!.id === "wellness" ? (
                              <motion.img 
                                src={wellnessIcon} 
                                alt="Wellness & Preventive Health" 
                                className="w-60 h-60 object-contain drop-shadow-2xl -mb-8"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                whileHover={{ scale: 1.05, rotate: -2 }}
                              />
                            ) : category!.id === "smarthealth" && expandedSection && expandedSection !== "smarthealth" ? (
                              <Cpu className="w-20 h-20 drop-shadow-2xl" strokeWidth={1.5} style={{ color: '#8BA4CD' }} />
                            ) : category!.id === "smarthealth" ? (
                              <motion.img 
                                src={nextGenIcon} 
                                alt="Next-Gen HealthTech" 
                                className="w-60 h-60 object-contain drop-shadow-2xl -mb-16"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                whileHover={{ scale: 1.05, rotate: 2 }}
                              />
                            ) : category!.id === "advisory" && expandedSection && expandedSection !== "advisory" ? (
                              <Search className="w-20 h-20 drop-shadow-2xl" strokeWidth={1.5} style={{ color: '#868EAD' }} />
                            ) : category!.id === "advisory" ? (
                              <motion.img 
                                src={advisoryIcon} 
                                alt="Strategy & Advisory" 
                                className="w-60 h-60 object-contain drop-shadow-2xl -mb-8"
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                whileHover={{ scale: 1.05, rotate: -2 }}
                              />
                            ) : (
                              <CategoryIcon />
                            )}
                          </div>
                          {/* Title - Hidden when another section is expanded */}
                          {!expandedSection && (
                            <div className="text-center space-y-2 transition-all duration-300">
                              <h3 className="text-2xl font-bold tracking-wide min-h-[96px] flex items-center justify-center" style={{ 
                                color: category!.id === "telemedicine" ? '#78C6C2' : 
                                       category!.id === "wellness" ? '#66B3BB' :
                                       category!.id === "smarthealth" ? '#8BA4CD' :
                                       category!.id === "advisory" ? '#868EAD' : '#00858f'
                              }}>
                                {category!.title}
                              </h3>
                              <div className="w-12 h-1 rounded-full mx-auto" style={{ 
                                backgroundColor: category!.id === "telemedicine" ? 'rgba(120, 198, 194, 0.5)' : 
                                                category!.id === "wellness" ? 'rgba(102, 179, 187, 0.5)' :
                                                category!.id === "smarthealth" ? 'rgba(170, 196, 238, 0.5)' :
                                                category!.id === "advisory" ? 'rgba(134, 142, 173, 0.5)' : 'rgba(0, 133, 143, 0.5)'
                              }} />
                            </div>
                          )}
                        </div>

                        {/* Expanded state */}
                        <div className={`flex-col h-full animate-fade-in ${isExpanded ? 'flex' : 'hidden'}`}>
                          {/* Header */}
                          <motion.div initial={{
                        opacity: 0,
                        y: -20
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        duration: 0.5
                      }} className="mb-8 pb-6 border-b relative" style={{ 
                        borderColor: category!.id === "telemedicine" ? 'rgba(120, 198, 194, 0.2)' : 
                                     category!.id === "wellness" ? 'rgba(102, 179, 187, 0.2)' :
                                     category!.id === "smarthealth" ? 'rgba(170, 196, 238, 0.2)' :
                                     category!.id === "advisory" ? 'rgba(134, 142, 173, 0.2)' : 'rgba(0, 133, 143, 0.2)'
                      }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSection(null);
                                setIsOtherSectionHovered(false);
                              }}
                              className="absolute top-0 right-0 p-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                              aria-label="Close section"
                            >
                              <X className="w-6 h-6" strokeWidth={1.5} style={{ 
                                color: category!.id === "telemedicine" ? '#78C6C2' : 
                                       category!.id === "wellness" ? '#66B3BB' :
                                       category!.id === "smarthealth" ? '#8BA4CD' :
                                       category!.id === "advisory" ? '#868EAD' : '#00858f'
                              }} />
                            </button>
                            <div className="flex items-center gap-4 mb-4">
                              <div>
                                <div className="flex items-center gap-3 mb-1">
                                  {category!.id === "telemedicine" && (
                                    <div className="p-3 backdrop-blur-sm rounded-2xl" style={{ 
                                      backgroundColor: 'rgba(120, 198, 194, 0.2)' 
                                    }}>
                                      <Stethoscope className="w-10 h-10" strokeWidth={1.5} style={{ color: '#78C6C2' }} />
                                    </div>
                                  )}
                                  {category!.id === "wellness" && (
                                    <div className="p-3 backdrop-blur-sm rounded-2xl" style={{ 
                                      backgroundColor: 'rgba(102, 179, 187, 0.2)' 
                                    }}>
                                      <Activity className="w-10 h-10" strokeWidth={1.5} style={{ color: '#66B3BB' }} />
                                    </div>
                                  )}
                                  {category!.id === "smarthealth" && (
                                    <div className="p-3 backdrop-blur-sm rounded-2xl" style={{ 
                                      backgroundColor: 'rgba(139, 164, 205, 0.2)' 
                                    }}>
                                      <Cpu className="w-10 h-10" strokeWidth={1.5} style={{ color: '#8BA4CD' }} />
                                    </div>
                                  )}
                                  {category!.id === "advisory" && (
                                    <div className="p-3 backdrop-blur-sm rounded-2xl" style={{ 
                                      backgroundColor: 'rgba(134, 142, 173, 0.2)' 
                                    }}>
                                      <Search className="w-10 h-10" strokeWidth={1.5} style={{ color: '#868EAD' }} />
                                    </div>
                                  )}
                                  <h3 className="text-3xl font-bold tracking-tight" style={{ 
                                    color: category!.id === "telemedicine" ? '#78C6C2' : 
                                           category!.id === "wellness" ? '#66B3BB' :
                                           category!.id === "smarthealth" ? '#8BA4CD' :
                                           category!.id === "advisory" ? '#868EAD' : '#00858f'
                                  }}>
                                    {category!.title}
                                  </h3>
                                </div>
                                <p className="text-sm font-medium" style={{ 
                                  color: category!.id === "telemedicine" ? '#78C6C2' : 
                                         category!.id === "wellness" ? '#66B3BB' :
                                         category!.id === "smarthealth" ? '#8BA4CD' :
                                         category!.id === "advisory" ? '#868EAD' : '#00858f'
                                }}>
                                  {category!.services.length} {t.services.servicesAvailable}
                                </p>
                              </div>
                            </div>
                            <p className="text-base leading-relaxed" style={{ 
                              color: category!.id === "telemedicine" ? '#78C6C2' : 
                                     category!.id === "wellness" ? '#66B3BB' :
                                     category!.id === "smarthealth" ? '#8BA4CD' :
                                     category!.id === "advisory" ? '#868EAD' : '#00858f'
                            }}>
                              {category!.description}
                            </p>
                          </motion.div>

                          {/* Service Buttons - scrollable */}
                          <div className="flex-1 overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-track-transparent" style={{ 
                            scrollbarColor: category!.id === "telemedicine" ? 'rgba(120, 198, 194, 0.2) transparent' : 
                                           category!.id === "wellness" ? 'rgba(102, 179, 187, 0.2) transparent' :
                                           category!.id === "smarthealth" ? 'rgba(170, 196, 238, 0.2) transparent' :
                                           category!.id === "advisory" ? 'rgba(134, 142, 173, 0.2) transparent' : 'rgba(0, 133, 143, 0.2) transparent'
                          }}>
                            {category!.services.map((service, serviceIndex) => {
                          return <Button 
                                  key={service.title}
                                  variant="outline" 
                                  className="w-full h-auto min-h-[80px] py-4 px-5 text-white whitespace-normal break-words normal-case backdrop-blur-md hover:brightness-110 active:brightness-125 border-0 text-left transition-all duration-300 shadow-md hover:shadow-xl active:shadow-2xl flex flex-col items-start justify-center gap-2 rounded-xl"
                                  style={{
                                    backgroundColor: category!.id === "telemedicine" ? '#78C6C2' : 
                                                    category!.id === "wellness" ? '#66B3BB' :
                                                    category!.id === "smarthealth" ? '#8BA4CD' :
                                                    category!.id === "advisory" ? '#868EAD' : '#00858f'
                                  }}
                                  onClick={() => handleLearnMore(service.key)}
                                >
                                  <span className="text-base font-bold text-white w-full whitespace-normal break-words leading-snug">
                                    {service.title}
                                  </span>
                                  <span className="text-sm text-white/90 leading-relaxed w-full whitespace-normal break-words">
                                    {service.description}
                                  </span>
                                </Button>;
                        })}
                          </div>
                        </div>
                      </div>
                    </motion.div>;
              })}
              </div>

              {/* Mobile & Tablet: Vertical accordion layout */}
              <Accordion type="multiple" className="lg:hidden space-y-6">
                {[serviceCategories.find(cat => cat.id === "telemedicine"), serviceCategories.find(cat => cat.id === "wellness"), serviceCategories.find(cat => cat.id === "smarthealth"), serviceCategories.find(cat => cat.id === "advisory")].filter(Boolean).map((category, categoryIndex) => {
                const CategoryIcon = category!.icon;
                const solidColors = ['rgba(120, 198, 194, 0.35)', 'rgba(102, 179, 187, 0.35)', 'rgba(200, 221, 255, 0.35)', 'rgba(134, 142, 173, 0.35)'];
                return <AccordionItem key={category!.id} value={category!.id} className="backdrop-blur-sm border-0 rounded-2xl overflow-hidden relative shadow-lg" style={{
                  backgroundColor: solidColors[categoryIndex]
                }}>
                      <AccordionTrigger 
                        className="px-6 py-5 hover:no-underline" 
                        chevronColor={
                          category!.id === "telemedicine" ? '#78C6C2' : 
                          category!.id === "wellness" ? '#66B3BB' :
                          category!.id === "smarthealth" ? '#8BA4CD' :
                          category!.id === "advisory" ? '#868EAD' : '#00858f'
                        }
                      >
                        <div className="relative z-10 flex items-center gap-4 w-full">
                          <div className="text-left">
                            <h2 className="text-2xl font-bold py-[5px]" style={{ 
                              color: category!.id === "telemedicine" ? '#78C6C2' : 
                                     category!.id === "wellness" ? '#66B3BB' :
                                     category!.id === "smarthealth" ? '#8BA4CD' :
                                     category!.id === "advisory" ? '#868EAD' : '#00858f'
                            }}>
                              {category!.title}
                            </h2>
                            <p className="text-base py-[2px]" style={{ 
                              color: category!.id === "telemedicine" ? '#78C6C2' : 
                                     category!.id === "wellness" ? '#66B3BB' :
                                     category!.id === "smarthealth" ? '#8BA4CD' :
                                     category!.id === "advisory" ? '#868EAD' : '#00858f'
                            }}>
                              {category!.description}
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      
                      <AccordionContent className="px-6 pb-6 pt-2 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                        <div className="flex flex-col gap-4">
                          {category!.services.map((service, serviceIndex) => {
                        return <motion.div key={service.title} initial={{
                          opacity: 0,
                          y: 20
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={{
                          duration: 0.6,
                          delay: serviceIndex * 0.1
                        }} className="w-full">
                                <Button 
                                  variant="outline" 
                                  className="w-full h-auto min-h-[80px] py-4 px-5 text-white whitespace-normal break-words normal-case backdrop-blur-md hover:brightness-110 active:brightness-125 border-0 text-left transition-all duration-300 shadow-md hover:shadow-xl active:shadow-2xl flex flex-row items-center justify-between gap-4 rounded-xl overflow-hidden"
                                  style={{
                                    backgroundColor: category!.id === "telemedicine" ? '#78C6C2' : 
                                                    category!.id === "wellness" ? '#66B3BB' :
                                                    category!.id === "smarthealth" ? '#8BA4CD' :
                                                    category!.id === "advisory" ? '#868EAD' : '#00858f'
                                  }}
                                  onClick={() => handleLearnMore(service.key)}
                                >
                                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                                    <span className="text-sm sm:text-base font-bold text-white w-full whitespace-normal break-words hyphens-auto leading-snug" style={{ wordBreak: 'break-word' }}>
                                      {service.title}
                                    </span>
                                    <span className="text-sm text-white/90 leading-relaxed w-full whitespace-normal break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                                      {service.description}
                                    </span>
                                  </div>
                                  <ChevronRight className="w-5 h-5 flex-shrink-0 text-white/70" />
                                </Button>
                              </motion.div>;
                      })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>;
              })}
              </Accordion>
            </motion.div>

            {/* Call to Action */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.8
          }} className="text-center mt-20 bg-white/80 backdrop-blur-sm border border-primary/10 rounded-2xl p-12 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/3 to-accent-teal/5" />
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-primary mb-6 py-[10px]">
                  {t.services.cta.title}
                </h3>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto py-[2px]">
                  {t.services.cta.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="outline" size="lg" className="bg-white/50 hover:bg-primary/10 border-primary/30 hover:border-primary/50 text-primary">
                    <a href="mailto:info@vidavo.eu">{t.services.cta.scheduleConsultation}</a> 
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />

      <ServiceModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        serviceKey={selectedService || undefined}
        fallbackData={selectedService && serviceDetails[selectedService as keyof typeof serviceDetails] ? serviceDetails[selectedService as keyof typeof serviceDetails] : undefined}
      />
    </div>;
};
export default Services;